import {
  SHIPPING_ROUTES,
  orderResponseSchema,
  shipmentLabelResponseSchema,
  shippingQuoteResponseSchema,
  shippingWalletResponseSchema,
  type CreateShipmentRequest,
  type Order,
  type ShippingDimensions,
  type ShippingQuoteOption,
  type ShippingQuoteRequest,
} from "@nh/shared";
import { authFetch } from "@/shared/api/session";

/**
 * El envío de una orden, con Trasso, visto desde el panel: cotizar,
 * generar la guía, pedir la etiqueta FRESCA, refrescar el estado,
 * cancelar, y el saldo del wallet.
 *
 * `baseUrl: ""` porque las rutas del contrato ya traen `/api`.
 */
const NO_PREFIX = { baseUrl: "" } as const;

export interface ShippingQuote {
  quoteId: string;
  options: ShippingQuoteOption[];
  package: ShippingDimensions;
  expiresAt: string;
}

export function quoteShipping(
  orderId: string,
  input: ShippingQuoteRequest,
): Promise<ShippingQuote> {
  return authFetch(SHIPPING_ROUTES.adminQuote(orderId), shippingQuoteResponseSchema, {
    ...NO_PREFIX,
    method: "POST",
    body: input,
  });
}

export async function createShipment(
  orderId: string,
  input: CreateShipmentRequest,
): Promise<Order> {
  const data = await authFetch(SHIPPING_ROUTES.adminCreate(orderId), orderResponseSchema, {
    ...NO_PREFIX,
    method: "POST",
    body: input,
  });
  return data.order;
}

/**
 * La URL del PDF se pide en el MOMENTO de abrirla: la firmada de Trasso
 * vive 1 hora y trae los datos del destinatario — jamás se guarda ni se
 * comparte por correo.
 */
export async function fetchLabelUrl(orderId: string): Promise<string> {
  const data = await authFetch(
    SHIPPING_ROUTES.adminLabel(orderId),
    shipmentLabelResponseSchema,
    NO_PREFIX,
  );
  return data.url;
}

export async function refreshShipment(orderId: string): Promise<Order> {
  const data = await authFetch(
    SHIPPING_ROUTES.adminRefresh(orderId),
    orderResponseSchema,
    { ...NO_PREFIX, method: "POST" },
  );
  return data.order;
}

export async function cancelShipment(orderId: string): Promise<Order> {
  const data = await authFetch(
    SHIPPING_ROUTES.adminCancel(orderId),
    orderResponseSchema,
    { ...NO_PREFIX, method: "POST" },
  );
  return data.order;
}

export interface ShippingWallet {
  currency: string;
  availableCents: number;
  updatedAt?: string;
}

export function getShippingWallet(): Promise<ShippingWallet> {
  return authFetch(SHIPPING_ROUTES.adminWallet, shippingWalletResponseSchema, NO_PREFIX);
}
